import React from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  formatCurrencyWithCents,
  parseCurrencyCents,
  Plus,
  Search,
  Select,
} from '@bancobarigui/barihaus';

import { FormLine, FormGrid } from './styles';
import { LoanType } from '../../enums/loan-type.enum';
import { createLoanTypeOptions } from '../../utils/createLoanTypeOptions';
import { Status } from '../../enums/proposal-status.enum';
import { validatePhoneNumber } from '../../utils/validatePhoneNumber';
import { validateCNPJ } from '../../utils/fieldsValidation';

interface ProposalFormProps {
  // Props do primeiro form (busca)
  formValues: any;
  handleChangeProposalNumber: (e: any) => void;
  isLoading: boolean;
  handleSearchProposal: (proposalNumber: string) => void;
  proposalSearchForm: any;

  // Props do segundo form (dados)
  form: any;
  handleClientNameChange: (e: any) => void;
  enableFields: boolean;
  disableClientFields: boolean;
  handleChangeLoan: (value: any) => void;
  selectedLoanType: string;
  handleChange: (e: any) => void;
  showExtraFields: boolean;
  showEmailForInvoice: boolean;
  showPurchaseMargin: boolean;
  defaultValueConvenant: any;
  handleClientConvenantChange: (e: any) => void;
  handleChangePurchaseMargin: (e: any) => void;
  purchaseMargin: number;
  isSubmitLoading: boolean;
  isButtonEnabled: boolean;
  handleSubmit: (e: any) => void;
}

export const ProposalForm = ({
  formValues,
  handleChangeProposalNumber,
  isLoading,
  handleSearchProposal,
  proposalSearchForm,
  form,
  handleClientNameChange,
  enableFields,
  disableClientFields,
  handleChangeLoan,
  selectedLoanType,
  handleChange,
  showExtraFields,
  showEmailForInvoice,
  showPurchaseMargin,
  defaultValueConvenant,
  handleClientConvenantChange,
  handleChangePurchaseMargin,
  purchaseMargin,
  isSubmitLoading,
  isButtonEnabled,
  handleSubmit,
}: ProposalFormProps) => {
  const optionsLoan = createLoanTypeOptions([
    LoanType.NEW_LOAN,
    LoanType.LOAN_REFINANCE,
    LoanType.NEW_LOAN_PORTABILITY,
    LoanType.LOAN_REFINANCE_PORTABILITY,
  ]);
  const optionsLoanConsignedCard = createLoanTypeOptions([LoanType.NEW_LOAN, LoanType.LOAN_REFINANCE]);
  return (
    <>
      <Flex className="mt-4">
        <Form
          form={proposalSearchForm}
          layout="horizontal"
          requiredMark={false}
          className="container-form-proposal flex flex-row items-end w-full"
        >
          <FormLine>
            <Form.Item
              label="Número da proposta *"
              name="proposalNumber"
              rules={[{ required: true, message: 'Preencha o número da proposta' }]}
              layout="vertical"
            >
              <Input
                placeholder="Ex: 0000000"
                onChange={handleChangeProposalNumber}
                value={formValues.proposalNumber || ''}
                mask="0000000"
              />
            </Form.Item>
            <Form.Item>
              <Button
                htmlType="button"
                loading={isLoading}
                buttonColor="secondary"
                variant="outlined"
                onClick={() => handleSearchProposal(formValues.proposalNumber)}
                icon={isLoading ? '' : <Search fill="var(--colors-base-black-950)" />}
                iconPlacement="end"
              >
                Buscar proposta
              </Button>
            </Form.Item>
          </FormLine>
        </Form>
      </Flex>
      <Form form={form} layout="horizontal" requiredMark={false}>
        <FormGrid>
          <Form.Item
            label="Nome completo"
            name="name"
            layout="vertical"
            rules={[{ required: true, message: 'Insira o nome do cliente' }]}
          >
            <Input placeholder="Nome do cliente" disabled={true} onChange={handleClientNameChange} />
          </Form.Item>
          <Form.Item
            label="Tipo de empréstimo"
            name="selectedLoanType"
            layout="vertical"
            rules={[{ required: true, message: 'Selecione um tipo de empréstimo' }]}
          >
            <Select
              id="selectedLoanType"
              disabled={!enableFields}
              placeholder="Selecione uma opção"
              options={formValues.hasConsignCard ? optionsLoanConsignedCard : optionsLoan}
              onChange={(value) => handleChangeLoan(value)}
              value={String(selectedLoanType)}
            />
          </Form.Item>
          <Form.Item label="CPF" name="documentNumber" layout="vertical">
            <Input
              type="text"
              id="documentNumber"
              name="documentNumber"
              placeholder="000.000.000-00"
              disabled={true}
              onChange={handleChange}
            />
          </Form.Item>
          <Form.Item
            label="Celular"
            name="phoneNumber"
            layout="vertical"
            rules={[
              { required: true, message: 'Insira o celular do cliente' },
              {
                validator: (_, value) => {
                  const errorMessage = validatePhoneNumber(value || '');
                  return errorMessage ? Promise.reject(new Error(errorMessage)) : Promise.resolve();
                },
              },
            ]}
          >
            <Input
              id="phoneNumber"
              name="phoneNumber"
              value={formValues.phoneNumber || ''}
              mask="(00) 00000-0000"
              placeholder="(00) 00000 0000"
              onChange={handleChange}
              disabled={!enableFields || disableClientFields}
            />
          </Form.Item>
          <Form.Item
            className={!showExtraFields && !showEmailForInvoice && !showPurchaseMargin ? 'fullColumn' : ''}
            label="Convênio"
            name="cnpjConsigneeId"
            layout="vertical"
          >
            <Input
              placeholder="Convênio"
              disabled={true}
              value={defaultValueConvenant ? defaultValueConvenant.label : ''}
              onChange={handleClientConvenantChange}
            />
          </Form.Item>
          {/* Campos dinâmicos de acordo com o convênio */}
          {showEmailForInvoice && (
            <Form.Item
              label="Email"
              name="email"
              layout="vertical"
              validateTrigger="onBlur"
              rules={[
                { required: true, message: 'Insira o email do cliente' },
                {
                  validator: (_, value) => {
                    if (value) {
                      const atIndex = value.indexOf('@');
                      const afterAt = value.substring(atIndex + 1);
                      const lastDot = afterAt.lastIndexOf('.');
                      const extension = afterAt.substring(lastDot + 1);
                      if (
                        atIndex <= 0 ||
                        atIndex === value.length - 1 ||
                        !afterAt.includes('.') ||
                        extension.length < 2
                      ) {
                        return Promise.reject(new Error('Email deve ter um @ válido'));
                      }
                    }
                  },
                },
              ]}
            >
              <Input
                id="email"
                name="email"
                value={formValues.email || ''}
                type="email"
                onChange={handleChange}
                placeholder="Digite seu email"
                disabled={disableClientFields}
              />
            </Form.Item>
          )}
          {showExtraFields && (
            <Form.Item
              label="CNPJ"
              name="companyDocument"
              layout="vertical"
              rules={[
                { required: true, message: 'Insira o CNPJ da empresa' },
                {
                  validator: (_, value) => {
                    const errorMessage = validateCNPJ(value || '');
                    return errorMessage ? Promise.reject(new Error(errorMessage)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                id="companyDocument"
                name="companyDocument"
                type="text"
                mask={'00.000.000/0000-00'}
                placeholder={'Informe o CNPJ da empresa'}
                value={formValues.companyDocument || ''}
                onChange={handleChange}
                disabled={disableClientFields}
              />
            </Form.Item>
          )}
          {showExtraFields && (
            <Form.Item
              label="Razão Social"
              name="companyLegalName"
              layout="vertical"
              className="fullColumn"
              rules={[{ required: true, message: 'Insira a razão social da empresa' }]}
            >
              <Input
                id="companyLegalName"
                name="companyLegalName"
                type="text"
                placeholder="Informe a razão social da empresa"
                value={formValues.companyLegalName || ''}
                onChange={handleChange}
                disabled={disableClientFields}
              />
            </Form.Item>
          )}
          {showPurchaseMargin && (
            <Form.Item label="Valor da margem para compras" name="purchaseMargin" layout="vertical">
              <InputNumber
                id="purchaseMargin"
                name="purchaseMargin"
                onChange={(value) => handleChangePurchaseMargin(value)}
                value={purchaseMargin}
                controls={false}
                formatter={(value) =>
                  formatCurrencyWithCents(typeof value === 'number' ? value : parseCurrencyCents(value || 0))
                }
                parser={(value) => parseCurrencyCents(value || 0)}
                placeholder="0,00"
                prefix="R$"
                disabled={formValues.loanDetails.some(
                  (loanDetail: any) =>
                    loanDetail.proposalNumber === formValues.proposalNumber &&
                    loanDetail.purchaseMargin > 0 &&
                    formValues.status !== Status.PENDING_PURCHASE_MARGIN,
                )}
              />
            </Form.Item>
          )}
          <Form.Item>
            <Button
              buttonColor="primary"
              htmlType="submit"
              loading={isSubmitLoading}
              icon={
                isSubmitLoading ? (
                  ''
                ) : (
                  <Plus fill={isButtonEnabled ? 'var(--colors-base-black-0)' : 'rgba(0,0,0,0.25)'} />
                )
              }
              onClick={(e: any) => handleSubmit(e)}
              disabled={!isButtonEnabled || isSubmitLoading}
            >
              Adicionar proposta
            </Button>
          </Form.Item>
        </FormGrid>
      </Form>
    </>
  );
};

export default ProposalForm;
